import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import CampaignIcon from '@mui/icons-material/Campaign';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import AnimatedSection from '../components/AnimatedSection';

function Tracks() {
  return (
    <Container maxWidth="lg">
      {/* Hero Section with Image */}
      <AnimatedSection animation="fadeIn">
      <Box sx={{
        position: 'relative',
        textAlign: 'center',
        my: 4,
        py: 8,
        background: `
          linear-gradient(135deg, rgba(255, 215, 0, 0.8), rgba(245, 166, 35, 0.8)),
          url('/presentto.jpg')
        `,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        color: 'white',
        borderRadius: 4,
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          zIndex: 1
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 2 }}>
          <Typography
            variant="h2"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
              mb: 2,
              maxWidth: 900,
              mx: 'auto',
              textAlign: 'center'
            }}
          >
            The Lagos Impact Hackathon Tracks
          </Typography>
          <Typography
            variant="h6"
            component="p"
            sx={{
              maxWidth: 900,
              mx: 'auto',
              textAlign: 'center',
              color: 'white',
              textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
            }}
          >
            The Hackathon features dual-innovation tracks running in parallel, combining blockchain technology and strategic communication to drive real-world implementation.
          </Typography>
        </Box>
      </Box>
      </AnimatedSection>

      {/* Tracks Overview Copy */}
      <AnimatedSection animation="slideUp" delay={0.2}>
        <Box sx={{ my: { xs: 4, md: 6 } }}>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
            <strong>Tech for Impact (Hack-a-thon)</strong><br />
            Web3 developers, AI engineers, data scientists, and blockchain architects will form teams to design and build functional, open-source technologies aligned with the United Nations' Sustainable Development Goals. These solutions will address real-world challenges such as financial inclusion, climate resilience, and transparent governance, using advanced Web3 tools and blockchain networks, including Ethereum, ReefChain, Solana, and Aptos.
          </Typography>
          <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
            At the end of the demo presentations, top-performing teams will be recognized through a structured reward and support program. This includes cash prizes for the winning groups, as well as post-hackathon mentoring and ecosystem access to help them refine, pilot, and scale their solutions. Commendation awards will also recognize teams that demonstrate exceptional creativity, technical innovation, or potential for social impact, ensuring that every promising idea has a clear pathway for growth beyond the hackathon.
          </Typography>
            <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
              <strong>Marketing for Impact (Mark-a-thon)</strong><br />
              <Typography variant="body1" paragraph sx={{ textAlign: 'justify' }}>
                Leading brand strategists and marketing professionals build launch-ready communication and visibility campaigns for SDG-aligned and impact-driven organizations, empowering them to grow their reach, secure funding, and create lasting impact.
              </Typography>
              All participating professionals will be recognized for their creativity, strategic clarity, and potential for real-world impact. Recognition awards will celebrate their excellence and highlight the value of their contributions to advancing sustainable innovation and social good.
            </Typography>
        </Box>
      </AnimatedSection>

      {/* Tracks Grid - Devpost Inspired Card Layout */}
      <Box sx={{ mb: 8 }}>
        <AnimatedSection animation="slideUp" delay={0.25}>
        <Typography variant="h4" component="h2" textAlign="center" gutterBottom sx={{ mb: 4 }}>
          Choose Your Track
        </Typography>
        </AnimatedSection>

        <Grid container spacing={4}>
          {/* Tech for Impact Track Card */}
          <Grid item xs={12} md={6}>
            <AnimatedSection animation="slideRight" delay={0.3}>
            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.3 }}>
            <Card sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}>
              {/* Featured Badge */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 2
              }}>
                    <Chip
                      icon={<CodeIcon />}
                      label="Hack-a-thon"
                      sx={{
                        bgcolor: '#FFD700',
                        color: '#000',
                        fontWeight: 'bold',
                        '& .MuiChip-icon': { color: '#000' }
                      }}
                    />
              </Box>

              {/* Card Image */}
              <Box sx={{
                height: 200,
                background: `
                  linear-gradient(45deg, rgba(0, 100, 0, 0.8), rgba(46, 125, 50, 0.8)),
                  url('/home_hackathon.jpg')
                `,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <CodeIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                  Tech for Impact Track
                </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Design and build functional, open-source technologies aligned with the United Nations' Sustainable Development Goals.
                      </Typography>                {/* Stats Row */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PeopleIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        100+
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Participants
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <AccessTimeIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        48hrs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <EmojiEventsIcon sx={{ color: 'primary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        6
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Challenges
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Technologies */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Technologies:
                  </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        <Chip size="small" label="AI" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#E8F5E8', color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2E7D32' })} />
        <Chip size="small" label="Web3" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#E8F5E8', color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2E7D32' })} />
        <Chip size="small" label="Blockchain" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#E8F5E8', color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2E7D32' })} />
        <Chip size="small" label="Data Science" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(46,125,50,0.15)' : '#E8F5E8', color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#2E7D32' })} />
                  </Box>
                </Box>

                    {/* Removed summary paragraph as requested. */}
              </CardContent>
            </Card>
            </motion.div>
            </AnimatedSection>
          </Grid>

          {/* Marketing for Impact Track Card */}
          <Grid item xs={12} md={6}>
            <AnimatedSection animation="slideLeft" delay={0.3}>
            <motion.div whileHover={{ y: -8 }} transition={{ duration: 0.3 }}>
            <Card sx={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
            }}>
              {/* Popular Badge */}
              <Box sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                zIndex: 2
              }}>
                <Chip
                  icon={<CampaignIcon />}
                  label="Mark-a-thon"
                  sx={{
                    bgcolor: '#FFF3E0',
                    color: '#E65100',
                    fontWeight: 'bold',
                    '& .MuiChip-icon': { color: '#E65100' }
                  }}
                />
              </Box>

              {/* Card Image */}
              <Box sx={{
                height: 200,
                background: `
                  linear-gradient(45deg, rgba(255, 215, 0, 0.8), rgba(245, 166, 35, 0.8)),
                  url('/markathon.jpg')
                `,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative'
              }}>
                <CampaignIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>

              <CardContent sx={{ p: 3 }}>
                <Typography variant="h5" component="h3" gutterBottom sx={{ color: 'secondary.main', fontWeight: 'bold' }}>
                  Marketing for Impact Track
                </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Design launch-ready communication and visibility campaigns for SDG-aligned NGOs.
                      </Typography>                {/* Stats Row */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <PeopleIcon sx={{ color: 'secondary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        50+
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Participants
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <AccessTimeIcon sx={{ color: 'secondary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        48hrs
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Duration
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box sx={{ textAlign: 'center' }}>
                      <CampaignIcon sx={{ color: 'secondary.main', mb: 0.5 }} />
                      <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                        5
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        NGOs
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                {/* Skills */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    Skills:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip size="small" label="Strategy" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,215,0,0.15)' : '#FFF8E1', color: theme.palette.mode === 'dark' ? theme.palette.secondary.dark : '#F57C00' })} />
                    <Chip size="small" label="Branding" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,215,0,0.15)' : '#FFF8E1', color: theme.palette.mode === 'dark' ? theme.palette.secondary.dark : '#F57C00' })} />
                    <Chip size="small" label="Communications" sx={(theme) => ({ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,215,0,0.15)' : '#FFF8E1', color: theme.palette.mode === 'dark' ? theme.palette.secondary.dark : '#F57C00' })} />
                  </Box>
                </Box>

                {/* Removed summary paragraph as requested. */}
              </CardContent>
            </Card>
            </motion.div>
            </AnimatedSection>
          </Grid>
        </Grid>
      </Box>

      {/* Problem Statements */}
      <AnimatedSection animation="slideUp" delay={0.4}>
        <Box sx={{ mb: 8 }}>
          <Typography variant="h5" component="h3" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold' }}>
            Problem Statements
          </Typography>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Climate Resilience" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Transparent Governance" />
                </ListItem>
                 <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Financial Inclusion" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Healthcare Access" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Education Technology" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Sustainable Agriculture" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Box>
      </AnimatedSection>

      {/* Closing Mission Line */}
      <AnimatedSection animation="fadeIn" delay={0.55}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="body1" paragraph>
            Together, these tracks advance our mission: to move beyond ideas, transforming promising solutions into holistic, deployment-ready ventures with clear pathways to funding, adoption, and real-world adoption.
          </Typography>
        </Box>
      </AnimatedSection>

      {/* Call to Action */}
      <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'primary.main', color: 'white', borderRadius: 3 }}>
        <Typography variant="h4" component="h2" gutterBottom>
          Ready to Make an Impact?
        </Typography>
        <Typography variant="body1" paragraph>
          Join the elite ranks of Nigeria's most innovative problem-solvers.
        </Typography>
        <Button
          variant="contained"
          size="large"
          component={Link}
          to="/register"
          sx={{ 
            bgcolor: 'secondary.main', 
            color: 'white', 
            fontWeight: 'bold',
            border: '2px solid white',
            '&:hover': { 
              bgcolor: '#FFA000',
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
              border: '2px solid white',
              color: 'white'
            } 
          }}
        >
          Apply Now
        </Button>
      </Box>
    </Container>
  );
}

export default Tracks;
